import React, { useState, useEffect, useRef, createContext } from 'react'
import * as THREE from 'three'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import { dispatch, EVENTS_GAME } from '../../b.providers/EventsProvider/EventsProvider'

export const context = createContext()

const manager = new THREE.LoadingManager()
manager.onStart = function (url, itemsLoaded, itemsTotal) {
  dispatch(EVENTS_GAME.LOADING_START, { url, itemsLoaded, itemsTotal })
  // console.log('Started loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.')
}
manager.onLoad = function () {
  dispatch(EVENTS_GAME.LOADING_COMPLETE, null)
  // console.log('Loading complete!')
}
manager.onProgress = function (url, itemsLoaded, itemsTotal) {
  dispatch(EVENTS_GAME.LOADING_PROGRESS, { url, itemsLoaded, itemsTotal })
  // console.log('Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.')
}
manager.onError = function (url) {
  dispatch(EVENTS_GAME.LOADING_ERROR, { url })
  // console.log('There was an error loading ' + url)
}

const guessLoaderByFileType = url => {
  let filename
  try {
    const uri = new URL(url)
    filename = uri.pathname.replace(uri.search, '')
  } catch (e) {
    filename = url
  }
  const ext = filename.split('.').pop()
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'png':
      return THREE.ImageLoader
    case 'obj':
      return OBJLoader
    case 'glb':
      return GLTFLoader
    case 'mp3':
    case 'ogg':
    case 'wav':
      return THREE.AudioLoader
    case 'json':
      return FontLoader
    case 'mtl':
      return MTLLoader
    case 'fbx':
      return FBXLoader
    default:
      return THREE.Loader
  }
}

export default function AssetsManager({ children, assets, loadingIndicator }) {
  const assetsRefs = useRef({}).current
  const [loadComplete, setLoadComplete] = useState()
  manager.onLoad = function () {
    // console.log('Loading complete!')
    setLoadComplete(true)
  }
  const [state, setState] = useState({
    assetsList: {},
    ready: false,
  })
  useEffect(() => {
    if (assets) {
      const promises = Object.keys(assets).map(async k => {
        // load-only-once mechanism
        if (assetsRefs.hasOwnProperty(k)) return Promise.resolve()
        assetsRefs[k] = 'loading'
        // ========================
        if (typeof assets[k] === 'object' && assets[k].hasOwnProperty('obj') && assets[k].hasOwnProperty('mtl')) {
          // is obj + mtl
          return new Promise(res => {
            new MTLLoader(manager).load(assets[k].mtl, matCreator => {
              matCreator.preload()
              new OBJLoader(manager).setMaterials(matCreator).load(assets[k].obj, object => {
                assetsRefs[k] = object
                res(true)
              })
            })
          })
        } else {
          // traditional loading by guessed loader
          const LoaderConstructor = guessLoaderByFileType(assets[k])
          const loader = new LoaderConstructor(manager)
          return new Promise(res => {
            loader.load(assets[k], function (object) {
              assetsRefs[k] = object
              res(true)
            })
          })
        }
      })
      Promise.all(promises).then(() => {
        setState({ assetsList: assetsRefs, ready: true })
      })
    }
  }, [assets, assetsRefs])
  const content = loadComplete ? children : loadingIndicator
  return <context.Provider value={state}>{content}</context.Provider>
}
